import { AttestationModel } from '@/app/models/attestation/attestationModel'
import {
  useLoadingStore,
  selectLoading,
} from '@/modules/shared/stores/loadingStore'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { downloadFile } from '@/modules/printouts/services/BaseService'

const useCalendarService = () => {
  const startLoading = useLoadingStore((s) => s.startLoading)
  const getCalendar = async ({
    companyId,
    driverId,
    year,
  }: {
    companyId: string
    driverId: string
    year: number
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `/api/${companyId}/driverCalendar/${driverId}/${year}`,
      )
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }
  const changeDayNotes = (
    companyId: string,
    driverId: string,
    date: string,
    dayType: number,
    notes: string,
  ) =>
    axiosInstance.post(`api/${companyId}/driverCalendar/changeNotes`, {
      driverId,
      notes,
      dayType,
      date,
    })

  const changeDayTypes = (
    companyId: string,
    driverId: number | number,
    dates: Array<string>,
    dayType: number,
    notes: string,
  ) =>
    axiosInstance.post(`api/${companyId}/driverCalendar/changeDayTypes`, {
      driverId,
      notes,
      dayType,
      dates,
    })
  const changeVehicleDayTypes = (
    companyId: string,
    vehicleId: number | number,
    dates: Array<string>,
    dayType: number,
    notes: string,
  ) =>
    axiosInstance.post(`api/${companyId}/vehicleCalendar/changeDayTypes`, {
      vehicleId,
      notes,
      dayType,
      dates,
    })
  const restoreOriginalCardData = (
    companyId: string,
    id: string,
    dates: Array<string>,
    type: 'driver' | 'vehicle' = 'driver',
  ) =>
    type === 'vehicle'
      ? axiosInstance.post(
          `api/${companyId}/vehicleCalendar/restoreOriginalCardData`,
          { vehicleId: id, dates },
        )
      : axiosInstance.post(
          `api/${companyId}/driverCalendar/restoreOriginalCardData`,
          { driverId: id, dates },
        )

  const removeDigitalData = (
    companyId: string,
    id: string,
    dates: Array<string>,
    type: 'driver' | 'vehicle',
  ) =>
    type === 'vehicle'
      ? axiosInstance.post(
          `api/${companyId}/vehicleCalendar/removeDigitalData`,
          {
            vehicleId: id,
            dates,
          },
        )
      : axiosInstance.post(
          `api/${companyId}/driverCalendar/removeDigitalData`,
          {
            driverId: id,
            dates,
          },
        )
  const assignActivitiesToDriver = (
    companyId: string,
    vehicleId: number | number,
    dates: Array<string>,
  ) =>
    axiosInstance.post(
      `api/${companyId}/vehicleCalendar/assignActivitiesToDriver`,
      {
        vehicleId,
        dates,
      },
    )

  const removeBorderCrossings = (
    companyId: string,
    id: string,
    dates: Array<string>,
    type: 'driver' | 'vehicle',
  ) =>
    type === 'vehicle'
      ? axiosInstance.post(
          `api/${companyId}/vehicleCalendar/removeBorderCrossings`,
          { vehicleId: id, dates },
        )
      : axiosInstance.post(
          `api/${companyId}/driverCalendar/removeBorderCrossings`,
          { driverId: id, dates },
        )

  const removeTmsData = (
    companyId: string,
    id: string,
    dates: Array<string>,
    type: 'driver' | 'vehicle',
  ) =>
    type === 'vehicle'
      ? axiosInstance.post(`api/${companyId}/vehicleCalendar/removeTmsData`, {
          vehicleId: id,
          dates,
        })
      : axiosInstance.post(`api/${companyId}/driverCalendar/removeTmsData`, {
          driverId: id,
          dates,
        })

  const assignBorderCrossingsFromVehicle = (
    companyId: string,
    id: string,
    dates: Array<string>,
    type: 'driver' | 'vehicle',
  ) =>
    type === 'vehicle'
      ? axiosInstance.post(
          `api/${companyId}/vehicleCalendar/assignBorderCrossingsFromVehicle`,
          { vehicleId: id, dates },
        )
      : axiosInstance.post(
          `api/${companyId}/driverCalendar/assignBorderCrossingsFromVehicle`,
          { driverId: id, dates },
        )

  const autoAssignTmsDataToDriver = (
    companyId: string,
    id: string,
    dates: Array<string>,
    type: 'driver' | 'vehicle',
  ) =>
    type === 'vehicle'
      ? axiosInstance.post(
          `api/${companyId}/vehicleCalendar/autoAssignTmsDataToDriver`,
          { selectedVehicleId: id, selectedDates: dates },
        )
      : axiosInstance.post(
          `api/${companyId}/driverCalendar/autoAssignTmsDataToDriver`,
          { driverId: id, dates },
        )

  const autoAssignBorderCrossingsToDriver = (
    companyId: string,
    id: string,
    dates: Array<string>,
    type: 'driver' | 'vehicle',
  ) =>
    type === 'vehicle'
      ? axiosInstance.post(
          `api/${companyId}/vehicleCalendar/autoAssignBorderCrossingsToDriver`,
          { selectedVehicleId: id, selectedDates: dates },
        )
      : axiosInstance.post(
          `api/${companyId}/driverCalendar/autoAssignBorderCrossingsToDriver`,
          { driverId: id, dates },
        )

  const withLoading = async <T,>(request: () => Promise<T>): Promise<T> => {
    const stopLoading = startLoading()
    try {
      return await request()
    } finally {
      stopLoading()
    }
  }
  const copyActivitiesFromAnotherDay = (
    companyId: string,
    driverId: string,
    dates: Array<string>,
    sourceDriverId: string,
    sourceDate: string,
  ) =>
    withLoading(() =>
      axiosInstance.post<boolean>(
        `api/${companyId}/driverCalendar/copyActivitiesFromAnotherDay`,
        { driverId, dates, sourceDriverId, sourceDate },
      ),
    )
  const removeImportedActivities = (
    companyId: string,
    id: string,
    dates: Array<string>,
  ) =>
    axiosInstance.post(
      `api/${companyId}/vehicleCalendar/removeImportedActivities`,
      {
        dates: dates,
        vehicleId: id,
      },
    )
  const checkCopyActivityChance = (
    companyId: string,
    driverId: string,
    dates: Array<string>,
    sourceDriverId?: string,
    sourceDate?: string,
  ) =>
    withLoading(() =>
      axiosInstance.post<{
        warningCode: string
        daysWithActivities: string[]
        daysWithAttestations: string[]
        sourceHasActivities: boolean | null
      }>(`api/${companyId}/driverCalendar/checkCopyActivityChance`, {
        driverId,
        dates,
        sourceDriverId,
        sourceDate,
      }),
    )

  const checkAttestationExistence = (
    companyId: string,
    driverId: string,
    dates: Array<string>,
  ) =>
    axiosInstance.post(
      `api/${companyId}/driverCalendar/checkAttestationExistence`,
      {
        driverId,
        dates,
      },
    )
  const getAttestationDefaults = async (
    companyId: string,
  ): Promise<Pick<
    AttestationModel,
    'issuer' | 'position' | 'createdPlace' | 'acceptedPlace'
  > | null> => {
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/driverCalendar/attestationDefaults`,
      )
      return res.data ?? null
    } catch (err) {
      console.error('Could not load attestation defaults', err)
      return null
    }
  }
  //  const generateAttestation = (
  //     companyId: string,
  //     attestation: AttestationModel,
  //   ) =>
  //     downloadFile(
  //       `api/${companyId}/driverCalendar/generateAttestation`,
  //       attestation,
  //       dispatch,
  //     )
  const generateAttestation = (
    { companyId, attestation, download = false }:
    { companyId: string, attestation: AttestationModel, download?: boolean },
  ) =>
    downloadFile(
      `api/${companyId}/driverCalendar/generateAttestation`,
      attestation,
      true,
      'application/pdf',
      download,
    )
  return {
    getCalendar,
    changeDayNotes,
    changeDayTypes,
    changeVehicleDayTypes,
    assignActivitiesToDriver,
    removeImportedActivities,
    restoreOriginalCardData,
    removeDigitalData,
    removeBorderCrossings,
    assignBorderCrossingsFromVehicle,
    copyActivitiesFromAnotherDay,
    checkCopyActivityChance,
    removeTmsData,
    autoAssignTmsDataToDriver,
    autoAssignBorderCrossingsToDriver,
    checkAttestationExistence,
    getAttestationDefaults,
    generateAttestation,
  }
}

export default useCalendarService
